import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { API_URL } from "../config/api";
import {
  Users,
  Receipt,
  TrendingUp,
  TrendingDown,
  Calendar,
  Upload,
  Plus,
  RefreshCw,
  Building2,
  DollarSign,
  Trash2,
  Check,
  Clock,
} from "lucide-react";


// Vertical colors
const VERTICAL_COLORS = {
  artcom: "bg-purple-100 text-purple-800",
  cargo_cfs: "bg-green-100 text-green-800",
  ep_holding: "bg-orange-100 text-orange-800",
  logistika: "bg-blue-100 text-blue-800",
  hmm: "bg-pink-100 text-pink-800",
  epac_hr: "bg-cyan-100 text-cyan-800",
};

export default function CashFlowPage() {
  const { token, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);

  // Data
  const [dashboard, setDashboard] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [vatEntries, setVatEntries] = useState([]);
  const [verticals, setVerticals] = useState({});

  // Selected period
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  // Modal states
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showVatModal, setShowVatModal] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null);
  const [editingVat, setEditingVat] = useState(null);

  // Form data
  const [salaryForm, setSalaryForm] = useState({
    vertical: "",
    period: selectedPeriod,
    gross_amount: 0,
    net_amount: 0,
    payment_date: "",
    status: "pending",
    notes: "",
  });

  const [vatForm, setVatForm] = useState({
    vertical: "",
    period: selectedPeriod,
    amount: 0,
    entry_type: "payable",
    due_date: "",
    status: "pending",
    notes: "",
  });

  // Fetch functions
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/cashflow/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboard(response.data);
      setVerticals(response.data.verticals || {});
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    }
  }, [token]);

  const fetchSalaries = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/cashflow/salaries`, {
        params: { period: selectedPeriod },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSalaries(response.data.entries || []);
    } catch (error) {
      console.error("Failed to fetch salaries:", error);
    }
  }, [token, selectedPeriod]);

  const fetchVat = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/cashflow/vat`, {
        params: { period: selectedPeriod },
        headers: { Authorization: `Bearer ${token}` },
      });
      setVatEntries(response.data.entries || []);
    } catch (error) {
      console.error("Failed to fetch VAT:", error);
    }
  }, [token, selectedPeriod]);

  // Refresh all
  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchSalaries(), fetchVat()]);
    setLoading(false);
  };

  // Initial load
  useEffect(() => {
    refreshAll();
  }, [selectedPeriod]);

  // Import handlers
  const handleImportSalary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/cashflow/import/salary?period=${selectedPeriod}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success(`Imported ${response.data.created + response.data.updated} salary entries`);
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleImportVat = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/cashflow/import/vat?period=${selectedPeriod}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success(`Imported ${response.data.created + response.data.updated} VAT entries`);
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  // Save handlers
  const handleSaveSalary = async () => {
    try {
      await axios.post(`${API_URL}/cashflow/salaries`, salaryForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Salary entry saved");
      setShowSalaryModal(false);
      refreshAll();
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  const handleSaveVat = async () => {
    try {
      await axios.post(`${API_URL}/cashflow/vat`, vatForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("VAT entry saved");
      setShowVatModal(false);
      refreshAll();
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  const handleDeleteSalary = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API_URL}/cashflow/salaries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      refreshAll();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteVat = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API_URL}/cashflow/vat/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      refreshAll();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Get period options
  const getPeriodOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = -3; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-US", { year: "numeric", month: "long" });
      options.push({ value, label });
    }
    return options;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 text-white px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="cashflow-title">
                <DollarSign className="h-6 w-6" />
                Cash Flow Tracking
              </h1>
              <p className="text-indigo-200 text-sm mt-1">
                Salary totals and VAT returns/payables
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Period selector */}
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
                <Calendar className="h-4 w-4" />
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="w-[160px] border-0 bg-transparent text-white focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getPeriodOptions().map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                size="sm"
                variant="outline"
                onClick={refreshAll}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="salaries" className="gap-2">
              <Users className="h-4 w-4" />
              Salaries
            </TabsTrigger>
            <TabsTrigger value="vat" className="gap-2">
              <Receipt className="h-4 w-4" />
              VAT (DDV)
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {dashboard ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Salary - Gross</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-slate-700">
                        {formatCurrency(dashboard.salary?.current?.gross)}
                      </div>
                      <p className="text-sm text-slate-500">{dashboard.current_period}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Salary - Net</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-blue-600">
                        {formatCurrency(dashboard.salary?.current?.net)}
                      </div>
                      <p className="text-sm text-slate-500">To be paid</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">VAT Payable</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-red-600">
                        {formatCurrency(dashboard.vat?.current?.payable)}
                      </div>
                      <p className="text-sm text-slate-500">To pay</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">VAT Refund</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(dashboard.vat?.current?.refund)}
                      </div>
                      <p className="text-sm text-slate-500">Expected</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed Lists */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Salary by Vertical */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Salaries by Company
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.salary?.entries?.length > 0 ? (
                        <div className="space-y-2">
                          {dashboard.salary.entries.map((e, idx) => (
                            <div key={idx} className={`p-3 rounded-lg ${VERTICAL_COLORS[e.vertical] || "bg-slate-100"}`}>
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{verticals[e.vertical]?.name || e.vertical}</span>
                                <div className="text-right">
                                  <div className="font-bold">{formatCurrency(e.net_amount)}</div>
                                  <div className="text-xs opacity-75">Gross: {formatCurrency(e.gross_amount)}</div>
                                </div>
                              </div>
                              {e.payment_date && (
                                <div className="text-xs mt-1 flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  Pay date: {e.payment_date}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No salary data for this period</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* VAT by Vertical */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Receipt className="h-5 w-5" />
                        VAT (DDV) by Company
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.vat?.entries?.length > 0 ? (
                        <div className="space-y-2">
                          {dashboard.vat.entries.map((e, idx) => (
                            <div key={idx} className={`p-3 rounded-lg ${VERTICAL_COLORS[e.vertical] || "bg-slate-100"}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{verticals[e.vertical]?.name || e.vertical}</span>
                                  <Badge className={e.entry_type === "refund" ? "bg-green-500" : "bg-red-500"}>
                                    {e.entry_type === "refund" ? "Refund" : "Pay"}
                                  </Badge>
                                </div>
                                <div className={`font-bold ${e.entry_type === "refund" ? "text-green-600" : "text-red-600"}`}>
                                  {e.entry_type === "refund" ? "+" : "-"}{formatCurrency(e.amount)}
                                </div>
                              </div>
                              {e.due_date && (
                                <div className="text-xs mt-1 flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  Due: {e.due_date}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No VAT data for this period</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Loading...</p>
              </div>
            )}
          </TabsContent>

          {/* Salaries Tab */}
          <TabsContent value="salaries">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Salary Totals</CardTitle>
                    <CardDescription>Net/Gross amounts per company with payment dates</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".msg"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleImportSalary(e.target.files[0])}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span><Upload className="h-4 w-4 mr-2" />Import .msg</span>
                      </Button>
                    </label>
                    <Button 
                      size="sm"
                      onClick={() => {
                        setSalaryForm({ ...salaryForm, period: selectedPeriod, vertical: "" });
                        setShowSalaryModal(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />Add Entry
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {salaries.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No salary entries for {selectedPeriod}</p>
                ) : (
                  <div className="space-y-2">
                    {salaries.map((entry) => (
                      <div key={entry.id} className={`p-4 rounded-lg border ${VERTICAL_COLORS[entry.vertical] || "bg-slate-50"}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Building2 className="h-5 w-5" />
                            <div>
                              <div className="font-bold">{verticals[entry.vertical]?.name || entry.vertical}</div>
                              <div className="text-sm opacity-75">Period: {entry.period}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm text-slate-500">Gross</div>
                              <div className="font-bold">{formatCurrency(entry.gross_amount)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-slate-500">Net</div>
                              <div className="font-bold text-blue-600">{formatCurrency(entry.net_amount)}</div>
                            </div>
                            {entry.payment_date && (
                              <div className="text-right">
                                <div className="text-sm text-slate-500">Pay Date</div>
                                <div className="font-medium">{entry.payment_date}</div>
                              </div>
                            )}
                            <Badge className={entry.status === "paid" ? "bg-green-500" : "bg-yellow-500"}>
                              {entry.status}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteSalary(entry.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* VAT Tab */}
          <TabsContent value="vat">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>VAT Returns / Payables (DDV)</CardTitle>
                    <CardDescription>VAT amounts per company with due dates</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".msg"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleImportVat(e.target.files[0])}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span><Upload className="h-4 w-4 mr-2" />Import .msg</span>
                      </Button>
                    </label>
                    <Button 
                      size="sm"
                      onClick={() => {
                        setVatForm({ ...vatForm, period: selectedPeriod, vertical: "" });
                        setShowVatModal(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />Add Entry
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {vatEntries.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No VAT entries for {selectedPeriod}</p>
                ) : (
                  <div className="space-y-2">
                    {vatEntries.map((entry) => (
                      <div key={entry.id} className={`p-4 rounded-lg border ${VERTICAL_COLORS[entry.vertical] || "bg-slate-50"}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Receipt className="h-5 w-5" />
                            <div>
                              <div className="font-bold">{verticals[entry.vertical]?.name || entry.vertical}</div>
                              <div className="text-sm opacity-75">Period: {entry.period}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge className={entry.entry_type === "refund" ? "bg-green-500 text-white" : "bg-red-500 text-white"}>
                              {entry.entry_type === "refund" ? "Refund (vračilo)" : "Payable (plačilo)"}
                            </Badge>
                            <div className={`text-xl font-bold ${entry.entry_type === "refund" ? "text-green-600" : "text-red-600"}`}>
                              {entry.entry_type === "refund" ? "+" : "-"}{formatCurrency(entry.amount)}
                            </div>
                            {entry.due_date && (
                              <div className="text-right">
                                <div className="text-sm text-slate-500">Due Date</div>
                                <div className="font-medium">{entry.due_date}</div>
                              </div>
                            )}
                            <Badge className={entry.status === "paid" || entry.status === "received" ? "bg-green-500" : "bg-yellow-500"}>
                              {entry.status}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteVat(entry.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Salary Modal */}
      <Dialog open={showSalaryModal} onOpenChange={setShowSalaryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Salary Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Company</Label>
              <Select value={salaryForm.vertical} onValueChange={(v) => setSalaryForm({ ...salaryForm, vertical: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(verticals).map(([id, v]) => (
                    <SelectItem key={id} value={id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gross Amount (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={salaryForm.gross_amount}
                  onChange={(e) => setSalaryForm({ ...salaryForm, gross_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Net Amount (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={salaryForm.net_amount}
                  onChange={(e) => setSalaryForm({ ...salaryForm, net_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={salaryForm.payment_date}
                onChange={(e) => setSalaryForm({ ...salaryForm, payment_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSalaryModal(false)}>Cancel</Button>
            <Button onClick={handleSaveSalary}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VAT Modal */}
      <Dialog open={showVatModal} onOpenChange={setShowVatModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add VAT Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Company</Label>
              <Select value={vatForm.vertical} onValueChange={(v) => setVatForm({ ...vatForm, vertical: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(verticals).map(([id, v]) => (
                    <SelectItem key={id} value={id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={vatForm.entry_type} onValueChange={(v) => setVatForm({ ...vatForm, entry_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="payable">Payable (plačilo)</SelectItem>
                  <SelectItem value="refund">Refund (vračilo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={vatForm.amount}
                onChange={(e) => setVatForm({ ...vatForm, amount: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={vatForm.due_date}
                onChange={(e) => setVatForm({ ...vatForm, due_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVatModal(false)}>Cancel</Button>
            <Button onClick={handleSaveVat}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
